import * as React from 'react';
import { connect } from 'react-redux';
import { goBack, push, replace } from 'react-router-redux';
import { assign, find, merge } from 'lodash';
import * as moment from 'moment';
import * as validator from 'validator';
import { getCompanies } from '../../redux/actions/companies';
import { getBookingPayments, getPayments } from '../../redux/actions/payments';
import API from '../../redux/api';
import {
  getBooking,
  getRevision,
  updateBooking,
  sendHireAgreement,
} from '../../redux/actions/bookings';

import {
  BookingAccessories,
  BookingDrivers,
  BookingPayment,
  BookingPrices,
  BookingFinalForm,
  BookingUpdateDriversForm,
  BookingUpdateHireForm,
  BookingUpdatePricingForm,
  BookingChargeList,
  NoteList,
  Permission,
  RouteWrapper,
  PaymentTable,
  Button,
  Card,
  Icon,
  IconButton,
  Notification,
  Section,
  Box,
  DriverAvatar,
  BookingSidebar,
  HeadingSection,
} from '../../components';

interface Props {
  vehicleTypes: any;
  vehicles: any;
  companies: any;
  dispatch: Function;
  match: any;
  location: any;
}

interface State {
  id: number;
  revisionId?: number;
  booking: any;
  vehicleType: any;
  vehicle: any;
  duration: number;
  section: number;
  isReady: boolean;
  error: string | boolean;
  criticalError: string | boolean;
  formData: any;
  isDisabled: boolean;
  isFinalForm: boolean;
  isUnsaved: boolean;
  isEditing: boolean;
  isLoading: boolean;
  additionalCharges: Array<any>;
  vehicleCharges: Array<any>;
  tempAdditionalCharges: Array<any>;
  tempVehicleCharges: Array<any>;
  bookingUpdatePricingFormData?: any;
  showEndDateWarning: boolean;
  isShowingInsurance: boolean;
  hasPayment: boolean;
}

class Route extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    const search = this.props.location.search.replace('?', '');
    const params: any = {};
    search.split('&').map((param) => {
      const values = param.split('=');
      params[values[0]] = values[1];
    });

    const revisionId = Number(this.props.match.params.revisionId);

    this.state = {
      revisionId,
      id: parseInt(this.props.match.params.id, 10),
      booking: false,
      vehicleType: false,
      vehicle: false,
      duration: 0,
      section: parseInt(params.section, 10) || 0,
      isReady: false,
      error: false,
      criticalError: false,
      formData: false,
      isDisabled: false,
      isFinalForm: false,
      isUnsaved: false,
      isLoading: false,
      isEditing: false,
      additionalCharges: [],
      vehicleCharges: [],
      tempAdditionalCharges: [],
      tempVehicleCharges: [],
      showEndDateWarning: false,
      isShowingInsurance: false,
      hasPayment: false,
    };
    this.renderGeneral = this.renderGeneral.bind(this);
    this.renderDrivers = this.renderDrivers.bind(this);
    this.renderAccessories = this.renderAccessories.bind(this);
    this.renderPricing = this.renderPricing.bind(this);
    this.renderPaymentHistory = this.renderPaymentHistory.bind(this);
    this.renderNotes = this.renderNotes.bind(this);
    this.renderFinalForm = this.renderFinalForm.bind(this);
    this.renderActions = this.renderActions.bind(this);
  }

  /**
   * Update state with new booking changes
   */
  syncBooking(booking, onComplete = () => {}) {
    const duration =
      1 +
      moment(booking.end)
        .endOf('day')
        .diff(moment(booking.start).startOf('day'), 'd');

    const isDisabled =
      ['PENDING', 'UNASSIGNED', 'READY', 'ACTIVE', 'ON-HOLD'].indexOf(
        booking.status,
      ) === -1;

    // If accessories are attached calculate the accessory cost
    if (this.state.booking.accessories) {
      this.state.booking.accessory_cost = 0;
      this.state.booking.accessories.map((accessory) => {
        BookingAccessories.calculateAccessoryQuantity(
          this.state.booking.accessories,
          accessory,
          accessory.quantity,
          duration,
          this.state.booking.drivers,
        );
        this.state.booking.accessory_cost += accessory.total;
      });
    }

    // Calculate total cost
    booking.hire_cost = duration * booking.hire_cost_per_day;
    booking.driver_cost =
      Math.max(0, booking.drivers.length - 1) * booking.additional_driver_cost;
    booking.total_cost =
      Number(booking.hire_cost) +
      Number(booking.driver_cost) +
      Number(booking.accessory_cost) +
      Number(booking.extra_mileage_cost) +
      Number(booking.additional_charge_cost) +
      Number(booking.zero_rated_charge_cost) -
      Number(booking.company_insurance_discount) -
      Number(booking.discount);
    if (booking.mileage_after === null) {
      delete booking.mileage_after;
    }

    // Determine if we're clearing the company
    if (!validator.isNumeric(String(booking.company_id))) {
      booking.company_id = '';
    }

    if (booking.order_number === null) {
      booking.order_number = '';
    }

    this.setState(
      {
        booking,
        duration,
        isDisabled,
        id: booking.id,
        vehicle: booking.vehicle,
        vehicleType: find(this.props.vehicleTypes, {
          id: Number(booking.vehicle_type_id),
        }),
        isFinalForm: booking.status === 'ON-HOLD',
        error: false,
        isReady: true,
        additionalCharges: booking.additional_charges,
        vehicleCharges: booking.zero_rated_charges,
      },
      onComplete,
    );
  }

  /**
   * Fetch booking and relations from API
   */
  async componentDidMount() {
    if (this.state.revisionId) {
      const revision = await this.props.dispatch(
        getRevision(this.state.id, this.state.revisionId),
      );
      await this.setBooking(revision);
      this.setState({ isDisabled: true });
      return;
    }
    const response = await this.props.dispatch(getBooking(this.state.id));
    this.setBooking(response);
    this.getPayments();
  }

  private setBooking = async (response) => {
    if (response.error) {
      this.setState({ criticalError: response.error, isReady: true });
      return;
    }
    await this.props.dispatch(getCompanies());
    this.syncBooking(response.data);
  };

  private async getPayments() {
    const response = await this.props.dispatch(
      getBookingPayments(this.state.id),
    );
    this.setState({ hasPayment: false });

    if (response.data.length > 0) {
      this.setState({ hasPayment: true });
    }
    console.log(this.state.hasPayment);
  }

  /**
   * Save changes to the booking
   * @param {Object?} response
   */
  async handleUpdate(response, redirect: string | boolean = '/dashboard') {
    if (response.error) {
      this.setState({ error: response.error });
      return;
    }
    const isOnHold = response.data.status === 'ON-HOLD';
    if (redirect && !isOnHold) {
      this.props.dispatch(push(redirect));
      return;
    }
    this.syncBooking(response.data, () => {
      this.props.dispatch(replace(`/bookings/${response.data.id}`));
      this.setState({ isUnsaved: false, showEndDateWarning: false });
      if (
        response.data.status !== 'PENDING' &&
        Number(response.data.total_paid) < Number(response.data.total_cost) &&
        response.data.payment_status === 0
      ) {
        if (
          window.confirm(
            'There is outstanding payment due - do you want to pay for this?',
          )
        ) {
          this.setState({ section: 3 });
        }
      }
    });
  }

  private updateBooking = () => {
    return this.props.dispatch(
      updateBooking(
        this.state.id,
        merge({}, this.state.booking, {
          drivers: JSON.stringify(
            this.state.booking.drivers.map((driver) => driver.id),
          ),
          accessories: JSON.stringify(
            this.state.booking.accessories.map((accessory) => ({
              id: accessory.id,
              quantity: accessory.quantity,
              total: accessory.total,
            })),
          ),
          additional_charges: JSON.stringify(this.state.additionalCharges),
          zero_rated_charges: JSON.stringify(this.state.vehicleCharges),
        }),
      ),
    );
  };

  /**
   * Render the general tab
   */
  renderGeneral() {
    const booking = this.state.booking;
    if (this.state.isEditing) {
      return (
        <Card key="general-edit">
          <IconButton
            className="is-default is-pulled-right"
            icon="times"
            handleClick={() => {
              this.setState({ isEditing: false });
            }}
          >
            Cancel
          </IconButton>
          <h4 className="heading">Edit Hire</h4>
          <BookingUpdateHireForm
            booking={assign({}, booking)}
            isDisabled={this.state.isDisabled}
            handleUpdate={(data) => {
              const end = data.end || booking.end;
              const returnDate = data.return_date || booking.return_date;
              const returnDateChanged = !moment(booking.return_date).isSame(
                moment(data.return_date),
              );

              this.syncBooking(merge(booking, data), () => {
                this.setState({
                  isEditing: false,
                  isUnsaved: true,
                  showEndDateWarning:
                    end &&
                    returnDate &&
                    returnDateChanged &&
                    moment(end).isAfter(moment(returnDate)),
                });
              });
            }}
          />
        </Card>
      );
    }

    return (
      <Box key="general-view">
        <div className="media">
          <div className="media-content">
            <small className="has-text-grey">Starting On</small>
            <br />
            <strong>
              {moment(booking.start).format('dddd Do MMMM YYYY [at] HH:mm')}
            </strong>
            {booking.collection_date &&
              !moment(booking.collection_date).isSame(booking.start, 'day') && (
                <>
                  <br />
                  <small className="has-text-danger has-text-weight-bold">
                    Collected on{' '}
                    {moment(booking.collection_date).format(
                      'dddd Do MMMM YYYY [at] HH:mm',
                    )}
                  </small>
                </>
              )}
          </div>
        </div>
        <div className="media">
          <div className="media-content">
            <small className="has-text-grey">Returning On</small>
            <br />
            <strong>
              {booking.return_date
                ? moment(booking.return_date).format(
                    'dddd Do MMMM YYYY [at] HH:mm',
                  )
                : moment(booking.end).format('dddd Do MMMM YYYY')}
            </strong>
          </div>
        </div>
        <div className="media">
          <div className="media-content">
            <small className="has-text-grey">Length of Hire</small>
            <br />
            <strong>
              {this.state.duration} day{this.state.duration !== 1 && 's'}
            </strong>
          </div>
        </div>
        <div className="media">
          <div className="media-content">
            <small className="has-text-grey">Vehicle Type Requested</small>
            <br />
            <strong>{this.state.vehicleType.name}</strong>
          </div>
        </div>
        <div className="media">
          <div className="media-content">
            <small className="has-text-grey">Vehicle Assigned</small>
            <br />
            <strong>
              {this.state.vehicle ? this.state.vehicle.registration : '-'}
            </strong>
          </div>
        </div>
        <div className="media">
          <div className="media-content">
            <small className="has-text-grey">Mileage Before</small>
            <br />
            <strong>
              {booking.mileage_before ? booking.mileage_before : '-'}
            </strong>
          </div>
        </div>
        <div className="media">
          <div className="media-content">
            <small className="has-text-grey">Mileage Afterwards</small>
            <br />
            <strong>
              {booking.mileage_after ? booking.mileage_after : '-'}
            </strong>
          </div>
        </div>
        <div className="media">
          <div className="media-content">
            <small className="has-text-grey">Hire Cost Per Day</small>
            <br />
            <strong>£{Number(booking.hire_cost_per_day).toFixed(2)}</strong>
          </div>
          <div className="media-right">
            <small className="has-text-grey">
              RRP £{Number(booking.hire_cost_per_day_actual).toFixed(2)}
            </small>
          </div>
        </div>
        <div className="media">
          <div className="media-content">
            <small className="has-text-grey">Hire Cost</small>
            <br />
            <strong className="is-size-4">
              £{Number(booking.hire_cost).toFixed(2)}
            </strong>
          </div>
          <div className="media-right">
            <small className="has-text-grey">
              £{Number(booking.hire_cost_per_day).toFixed(2)} &times;{' '}
              {this.state.duration} day{this.state.duration !== 1 ? 's' : ''}
            </small>
          </div>
        </div>
        {!this.state.isDisabled && (
          <div className="buttons is-right">
            <IconButton
              className="is-default"
              icon="pencil"
              handleClick={() => this.setState({ isEditing: true })}
            >
              Edit
            </IconButton>
          </div>
        )}
      </Box>
    );
  }

  /**
   * Render the drivers tab
   */
  renderDrivers() {
    const booking = this.state.booking;
    return (
      <>
        <BookingDrivers
          disableFirstDriverRemove={booking.status !== 'PENDING'}
          booking={booking}
          isDisabled={this.state.isDisabled || this.state.isFinalForm}
          handleUpdate={(drivers) => {
            this.setState({ isUnsaved: true }, () => {
              this.syncBooking(merge(booking, { drivers }));
            });
          }}
        />
        <Card key="drivers">
          {this.state.isEditing ? (
            <BookingUpdateDriversForm
              booking={booking}
              handleUpdate={(data) => {
                this.syncBooking(merge(booking, data), () => {
                  this.setState({
                    isEditing: false,
                    isUnsaved: true,
                  });
                });
              }}
            />
          ) : (
            <table className="field">
              <tbody>
                <tr>
                  <th>Additional Drivers</th>
                  <td>
                    {booking.drivers !== undefined
                      ? Math.max(0, booking.drivers.length - 1)
                      : 0}
                  </td>
                </tr>
                <tr>
                  <th>Cost Per Additional Driver</th>
                  <td>
                    <div className="subtitle is-marginless">
                      £{Number(booking.additional_driver_cost).toFixed(2)}
                    </div>
                    <small>
                      £
                      {Number(booking.additional_driver_cost_actual).toFixed(2)}
                      &nbsp;per additional driver
                    </small>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          {!this.state.isDisabled && (
            <div className="field is-clearfix">
              <IconButton
                className="is-default is-pulled-right"
                icon={this.state.isEditing ? 'undo' : 'pencil'}
                handleClick={() => {
                  this.setState({ isEditing: !this.state.isEditing });
                }}
              >
                {this.state.isEditing ? 'Cancel' : 'Edit'}
              </IconButton>
            </div>
          )}
        </Card>
        <Box key="drivers-total">
          <div className="has-text-right">
            Driver Cost
            <div className="title is-marginless">
              £{Number(booking.driver_cost).toFixed(2)}
            </div>
          </div>
        </Box>
      </>
    );
  }

  /**
   * Render the accessories tab
   */
  renderAccessories() {
    const booking = this.state.booking;
    return (
      <div>
        <BookingAccessories
          booking={booking}
          duration={this.state.duration}
          isDisabled={this.state.isDisabled || this.state.isFinalForm}
          handleUpdate={(accessories, cost) => {
            this.setState({ isUnsaved: true }, () => {
              this.syncBooking(
                merge({}, booking, { accessories, accessory_cost: cost }),
              );
            });
          }}
        />
        <Box key="accessory-total">
          <div className="has-text-right">
            Accessory Cost
            <div className="title is-marginless">
              £{Number(booking.accessory_cost).toFixed(2)}
            </div>
          </div>
        </Box>
      </div>
    );
  }

  /**
   * Render the pricing tab
   */
  renderPricing() {
    if (this.state.isEditing) {
      return (
        <Card key="pricing-edit">
          <IconButton
            className="is-default is-pulled-right"
            icon="times"
            handleClick={() => {
              this.setState({ isEditing: false });
            }}
          >
            Cancel
          </IconButton>
          <h4 className="heading">Edit Pricing</h4>
          <BookingUpdatePricingForm
            updateOnChange
            booking={this.state.booking}
            handleUpdate={(bookingUpdatePricingFormData) => {
              this.setState({ bookingUpdatePricingFormData });
            }}
          />
          {this.state.isFinalForm && (
            <>
              <BookingChargeList
                booking={this.state.booking}
                title="Additional Charges"
                charges={this.state.additionalCharges}
                handleChange={(tempAdditionalCharges) => {
                  this.setState({ tempAdditionalCharges });
                }}
              />
              <BookingChargeList
                booking={this.state.booking}
                title="Zero Rated Charges"
                charges={this.state.vehicleCharges}
                handleChange={(tempVehicleCharges) => {
                  this.setState({ tempVehicleCharges });
                }}
              />
            </>
          )}
          <div className="buttons is-right">
            <Button
              handleClick={() => {
                const booking = this.state.booking;
                const additionalCharges = this.state.tempAdditionalCharges;
                const vehicleCharges = this.state.tempVehicleCharges;

                const getTotalCharge = (charges) =>
                  charges.length
                    ? charges
                        .map((charge) => Number(charge.amount))
                        .reduce((a, b) => a + b)
                    : 0;

                booking.additional_charge_cost = getTotalCharge(
                  additionalCharges,
                );
                booking.zero_rated_charge_cost = getTotalCharge(vehicleCharges);
                booking.additional_charges = additionalCharges;
                booking.zero_rated_charges = vehicleCharges;

                this.syncBooking(
                  merge(booking, this.state.bookingUpdatePricingFormData),
                  () => {
                    this.setState({
                      additionalCharges,
                      vehicleCharges,
                      isEditing: false,
                      isUnsaved: true,
                    });
                  },
                );
              }}
            >
              Apply
            </Button>
          </div>
        </Card>
      );
    }

    return (
      <>
        <Box key="pricing-view">
          <BookingPrices
            booking={this.state.booking}
            vehicleType={this.state.vehicleType}
          />
          <div className="buttons is-right">
            <IconButton
              className="is-default"
              icon="pencil"
              handleClick={() => {
                this.setState({ isEditing: true });
              }}
            >
              Edit
            </IconButton>
          </div>
        </Box>
        <>
          {this.state.isUnsaved ? (
            <Notification type="default">
              Payment methods have been disabled because of unsaved changes
            </Notification>
          ) : (
            <BookingPayment
              booking={this.state.booking}
              handleUpdate={(response) => {
                this.handleUpdate(response, false);
              }}
            />
          )}
        </>
      </>
    );
  }

  /**
   * Render the payments history tab for this booking
   */
  renderPaymentHistory() {
    return (
      <PaymentTable
        all
        isArchived={false}
        bookingId={this.state.booking.id}
        hideNotes
      />
    );
  }

  /**
   * Render the notes section
   */
  renderNotes() {
    return <NoteList id={this.state.id} resource="bookings" hideTitle />;
  }

  /**
   * Render the final form section
   */
  renderFinalForm() {
    return (
      <BookingFinalForm
        handleChange={(data) => {
          const end = data.end || this.state.booking.end;
          const returnDate = data.return_date || this.state.booking.return_date;
          const returnDateChanged = !moment(data.return_date).isSame(
            moment(this.state.booking.return_date),
          );

          this.setState({
            booking: merge({}, this.state.booking, data),
            isUnsaved: true,
            showEndDateWarning:
              end &&
              returnDate &&
              returnDateChanged &&
              moment(end).isAfter(moment(returnDate)),
          });
        }}
        handleChargesUpdate={(additionalCharges, vehicleCharges) => {
          const updateState: any = {
            isUnsaved: true,
            booking: merge({}, this.state.booking),
          };

          if (additionalCharges !== undefined) {
            updateState.additionalCharges = additionalCharges.filter(
              (charge) =>
                !find(this.state.booking.additionalCharges, { id: charge.id }),
            );
          }

          if (vehicleCharges !== undefined) {
            updateState.vehicleCharges = vehicleCharges.filter(
              (charge) =>
                !find(this.state.booking.vehicleCharges, { id: charge.id }),
            );
          }

          this.setState(updateState);
        }}
        booking={this.state.booking}
      />
    );
  }

  /**
   * Render actions within the booking
   */
  renderActions() {
    const isPreAuth = this.state.booking.payment_status === 4;
    return (
      <>
        <Card>
          <h4 className="heading">Hire Actions</h4>
          <br />
          {!this.state.isUnsaved && (
            <>
              {this.state.booking.is_final_form_complete &&
                this.state.booking.status === 'ON-HOLD' && (
                  <>
                    <a
                      onClick={() => {
                        const resolutionRoute = isPreAuth
                          ? `/payments/pre-auth/${this.state.id}`
                          : `/bookings/${this.state.id}/resolve`;
                        this.props.dispatch(push(resolutionRoute));
                      }}
                      className="media has-text-success"
                    >
                      <div className="media-left">
                        <Icon icon="check-circle" />
                      </div>
                      <div className="media-content">
                        <strong>
                          Resolve {isPreAuth ? 'Pre-Auth' : 'Payment'}
                        </strong>
                        <br />
                        <span className="has-text-grey">
                          Finalise the hire's payment
                        </span>
                      </div>
                    </a>
                    {!this.state.booking.has_final_form && (
                      <a
                        onClick={async () => {
                          this.setState({ isLoading: true });
                          await this.updateBooking();
                          this.props.dispatch(goBack());
                        }}
                        className="media has-text-danger"
                      >
                        <div className="media-left">
                          <Icon icon="pause" />
                        </div>
                        <div className="media-content">
                          <strong>Put on Hold</strong>
                          <br />
                          <span className="has-text-grey">
                            Move this into hires on hold
                          </span>
                        </div>
                      </a>
                    )}
                  </>
                )}
              <a
                onClick={async () => {
                  if (this.state.isLoading) {
                    return;
                  }
                  if (
                    window.confirm(
                      'Email the customer a copy of the hire agreement?',
                    )
                  ) {
                    this.setState({ isLoading: true });
                    await this.props.dispatch(sendHireAgreement(this.state.id));
                    this.setState({ isLoading: false });
                  }
                }}
                className="media"
              >
                <div className="media-left">
                  <Icon icon="envelope" />
                </div>
                <div className="media-content">
                  <strong>Email Hire Agreement</strong>
                  <br />
                  <span className="has-text-grey">
                    Email the customer a copy of their hire agreement
                  </span>
                </div>
              </a>
            </>
          )}
          <a
            onClick={() => {
              window.open(
                `${API.getURL()}/bookings/${this.state.id}/pdf/hire-agreement?${
                  API.authParams
                }`,
              );
            }}
            className="media"
          >
            <div className="media-left">
              <Icon icon="print" />
            </div>
            <div className="media-content">
              <strong>Hire Agreement</strong>
              <br />
              <span className="has-text-grey">
                View and print the hire agreement
              </span>
            </div>
          </a>
          <a
            className="media"
            onClick={() =>
              this.setState({
                isShowingInsurance: !this.state.isShowingInsurance,
              })
            }
          >
            <div className="media-left">
              <Icon icon="print" />
            </div>
            <div className="media-content">
              <strong>Insurance Forms</strong>
              <br />
              <span className="has-text-grey">
                View and print a driver's insurance form for this hire
              </span>
            </div>
          </a>
          {this.state.isShowingInsurance && (
            <>
              <Box>
                {this.state.booking.drivers.map((driver) => (
                  <a
                    className="media"
                    key={driver.id}
                    href={`${API.getURL()}/bookings/${
                      this.state.id
                    }/pdf/insurance-form/${driver.id}?${API.authParams}`}
                    target="_blank"
                  >
                    <div className="media-left">
                      <DriverAvatar driver={driver} size="32" />
                    </div>
                    <div className="media-content has-text-dark">
                      <strong>{driver.name}</strong>
                      <br />
                      <small className="has-text-grey">
                        Open Insurance Form
                      </small>
                    </div>
                  </a>
                ))}
              </Box>
            </>
          )}
        </Card>
      </>
    );
  }

  render() {
    let title = 'Booking';
    if (this.state.booking.contact_name !== null) {
      title += ` for ${this.state.booking.contact_name}`;
    }

    let renderSection;
    switch (this.state.section) {
      case 0:
        renderSection = this.renderGeneral;
        break;
      case 1:
        renderSection = this.renderDrivers;
        break;
      case 2:
        renderSection = this.renderAccessories;
        break;
      case 3:
        renderSection = this.renderPricing;
        break;
      case 4:
        renderSection = this.renderPaymentHistory;
        break;
      case 5:
        renderSection = this.renderNotes;
        break;
      case 6:
        renderSection = this.renderFinalForm;
        break;
      default:
        renderSection = () => {
          return null;
        };
    }

    const controls = [];
    if (
      !this.state.isDisabled &&
      this.state.booking.status === 'PENDING' &&
      Number(this.state.booking.total_paid) <
        Number(this.state.booking.total_cost) &&
      this.state.booking.payment_status === 0 &&
      !this.state.hasPayment
    ) {
      controls.push(
        <Permission key="delete" require="MANAGE_DELETIONS">
          <IconButton
            link={`/bookings/${this.state.booking.id}/delete`}
            className="is-danger is-outlined"
            icon="trash"
          >
            Delete
          </IconButton>
        </Permission>,
      );
    }
    controls.push(
      <IconButton
        key="return"
        icon="undo"
        className="is-default is-pulled-right"
        handleClick={() => {
          this.props.dispatch(goBack());
        }}
      >
        Return
      </IconButton>,
    );

    return (
      <RouteWrapper
        title={title}
        ready={this.state.isReady}
        error={this.state.criticalError}
        loading={this.state.isLoading}
        requireAuth
        breadcrumbs={[
          {
            link: `/bookings/status/${this.state.booking.status}`,
            text: 'Bookings',
          },
          {
            link: `/booking/${this.state.booking.id}`,
            text: this.state.booking.invoiceId,
          },
        ]}
      >
        <HeadingSection title={title} controls={controls} />
        <Section>
          <div className="columns is-centered">
            <div className="column is-12-desktop is-7-widescreen">
              <div className="tabs is-boxed">
                <ul>
                  {[
                    'Hire',
                    'Drivers',
                    'Accessories',
                    'Pricing',
                    'Payment History',
                    'Notes',
                    ...(this.state.isFinalForm ? ['Final Form'] : []),
                  ].map((name, index) => (
                    <li
                      key={name}
                      className={
                        this.state.section === index ? 'is-active' : ''
                      }
                    >
                      <a
                        onClick={() => {
                          this.setState({ section: index, isEditing: false });
                        }}
                      >
                        {name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              {renderSection()}
            </div>
            <div className="column is-12-desktop is-5-widescreen">
              {this.state.isUnsaved && (
                <Notification type="danger">
                  <Icon icon="exclamation-triangle" /> You have unsaved changes!
                </Notification>
              )}
              <BookingSidebar
                booking={this.state.booking}
                vehicle={this.state.vehicle}
                duration={this.state.duration}
              />
              {this.renderActions()}
              {this.state.isUnsaved ? (
                <div className="buttons is-right">
                  <Button
                    className="is-primary "
                    handleClick={async () => {
                      if (
                        this.state.showEndDateWarning &&
                        !confirm(
                          'You have updated the return date to before the end date. ' +
                            'Are you sure you wish to save this?',
                        )
                      ) {
                        return;
                      }
                      const response = await this.updateBooking();
                      this.handleUpdate(
                        response,
                        this.state.booking.status === 'PENDING'
                          ? '/dashboard'
                          : false,
                      );
                    }}
                  >
                    Save Changes
                  </Button>
                </div>
              ) : (
                <div className="buttons is-right">
                  <Button
                    className="is-primary "
                    handleClick={async () => {
                      this.props.dispatch(push('/dashboard'));
                    }}
                  >
                    Return to Dashboard
                  </Button>
                </div>
              )}
              {this.state.error !== false && <div className="field" />}
              {this.state.error !== false && (
                <div className="field">
                  <Notification type="danger">{this.state.error}</Notification>
                </div>
              )}
            </div>
          </div>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({
  vehicleTypes,
  vehicles,
  companies,
  auth: { user },
}) => ({
  vehicleTypes: vehicleTypes.vehicleTypes,
  vehicles: vehicles.vehicles,
  companies: companies.companies,
});

export default connect(mapStateToProps)(Route);
